import { useEffect, useRef, useState, useCallback } from "react";
import AgoraRTC from "agora-rtc-sdk-ng";
import { Mic, MicOff, Video, VideoOff, MonitorUp, PhoneOff, Loader2, Maximize2, Minimize2, RotateCw } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

function enterFs(el) {
  if (!el) return;
  if (document.fullscreenElement || document.webkitFullscreenElement) {
    (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
    return;
  }
  (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el);
}

function Tile({ containerRef, label, muted, isLocal }) {
  const tileRef = useRef(null);
  return (
    <div
      ref={tileRef}
      className="relative aspect-video bg-[#0B0C10] rounded-xl overflow-hidden border border-[#2E3242] group"
    >
      <div ref={containerRef} className="absolute inset-0 [&>div]:!bg-transparent" />
      <button
        onClick={() => enterFs(tileRef.current)}
        data-testid="tile-fullscreen"
        className="absolute top-2 right-2 w-8 h-8 rounded-md bg-black/60 backdrop-blur text-gray-200 flex items-center justify-center opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity hover:bg-black/80"
        title="Fullscreen this view"
      >
        <Maximize2 className="w-4 h-4" />
      </button>
      <div className="absolute bottom-2 left-2 flex items-center gap-1.5 px-2 py-1 rounded-md bg-black/60 backdrop-blur">
        {muted && <MicOff className="w-3 h-3 text-red-400" />}
        <span className="text-xs text-gray-100">{label}{isLocal ? " (you)" : ""}</span>
      </div>
    </div>
  );
}

export default function VideoRoom({ channel, title, onLeave }) {
  const clientRef = useRef(null);
  const localTracksRef = useRef({ audio: null, video: null });
  const screenTrackRef = useRef(null);
  const localElRef = useRef(null);
  const remoteEls = useRef({});
  const wrapperRef = useRef(null);

  const [remoteUsers, setRemoteUsers] = useState([]);
  const [joined, setJoined] = useState(false);
  const [joining, setJoining] = useState(true);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [displayName, setDisplayName] = useState("you");
  const [isFs, setIsFs] = useState(false);
  const [rotated, setRotated] = useState(false);
  const [cssRotate, setCssRotate] = useState(false);

  const leave = useCallback(async () => {
    const client = clientRef.current;
    try {
      if (document.fullscreenElement) document.exitFullscreen?.();
      try { window.screen?.orientation?.unlock?.(); } catch (e) { /* noop */ }
      if (screenTrackRef.current) {
        screenTrackRef.current.stop();
        screenTrackRef.current.close();
        screenTrackRef.current = null;
      }
      const { audio, video } = localTracksRef.current;
      audio?.stop(); audio?.close();
      video?.stop(); video?.close();
      localTracksRef.current = { audio: null, video: null };
      if (client) await client.leave();
    } catch (e) {
      /* ignore */
    }
    setJoined(false);
    onLeave?.();
  }, [onLeave]);

  useEffect(() => {
    const onFsChange = () => setIsFs(!!(document.fullscreenElement || document.webkitFullscreenElement));
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const client = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
    clientRef.current = client;

    const refresh = () => setRemoteUsers([...client.remoteUsers]);

    client.on("user-published", async (u, mediaType) => {
      await client.subscribe(u, mediaType);
      if (mediaType === "audio") u.audioTrack?.play();
      refresh();
      if (mediaType === "video") {
        setTimeout(() => {
          const el = remoteEls.current[u.uid];
          if (el && u.videoTrack) u.videoTrack.play(el);
        }, 60);
      }
    });
    client.on("user-unpublished", refresh);
    client.on("user-joined", refresh);
    client.on("user-left", refresh);

    (async () => {
      try {
        const { data } = await api.post("/rtc/token", { channel });
        if (cancelled) return;
        setDisplayName(data.displayName || "you");
        await client.join(data.appId, data.channel, data.token, data.uid);
        const [mic, cam] = await AgoraRTC.createMicrophoneAndCameraTracks(
          {}, { encoderConfig: "720p_1" }
        );
        localTracksRef.current = { audio: mic, video: cam };
        if (localElRef.current) cam.play(localElRef.current);
        await client.publish([mic, cam]);
        setJoined(true);
      } catch (e) {
        console.error("join failed", e);
        toast.error("Could not join the call. Check camera / mic permissions.");
        onLeave?.();
      } finally {
        if (!cancelled) setJoining(false);
      }
    })();

    return () => {
      cancelled = true;
      client.removeAllListeners();
      const { audio, video } = localTracksRef.current;
      audio?.stop(); audio?.close();
      video?.stop(); video?.close();
      screenTrackRef.current?.stop();
      screenTrackRef.current?.close();
      client.leave().catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel]);

  useEffect(() => {
    remoteUsers.forEach((u) => {
      const el = remoteEls.current[u.uid];
      if (el && u.videoTrack) u.videoTrack.play(el);
    });
  }, [remoteUsers]);

  // (re)attach LOCAL video only after the tile is mounted (fixes black tile).
  useEffect(() => {
    if (!joined || !localElRef.current) return;
    const track = sharing ? screenTrackRef.current : localTracksRef.current.video;
    if (track) track.play(localElRef.current);
  }, [joined, sharing, camOn, remoteUsers.length, rotated]);

  const toggleMic = async () => {
    const mic = localTracksRef.current.audio;
    if (!mic) return;
    await mic.setEnabled(!micOn);
    setMicOn((v) => !v);
  };
  const toggleCam = async () => {
    const cam = localTracksRef.current.video;
    if (!cam) return;
    await cam.setEnabled(!camOn);
    setCamOn((v) => !v);
  };
  const toggleScreen = async () => {
    const client = clientRef.current;
    if (!sharing) {
      try {
        const screenTrack = await AgoraRTC.createScreenVideoTrack({ encoderConfig: "1080p_1" }, "disable");
        screenTrackRef.current = screenTrack;
        if (localTracksRef.current.video) await client.unpublish(localTracksRef.current.video);
        await client.publish(screenTrack);
        if (localElRef.current) screenTrack.play(localElRef.current);
        screenTrack.on("track-ended", stopScreen);
        setSharing(true);
      } catch (e) {
        toast.error("Screen share cancelled");
      }
    } else {
      await stopScreen();
    }
  };
  const stopScreen = async () => {
    const client = clientRef.current;
    if (!screenTrackRef.current) return;
    try {
      await client.unpublish(screenTrackRef.current);
    } catch (e) { /* ignore */ }
    screenTrackRef.current.stop();
    screenTrackRef.current.close();
    screenTrackRef.current = null;
    const cam = localTracksRef.current.video;
    if (cam) {
      await client.publish(cam);
      if (localElRef.current) cam.play(localElRef.current);
    }
    setSharing(false);
  };

  const toggleRotate = async () => {
    if (rotated) {
      setRotated(false);
      setCssRotate(false);
      try { window.screen?.orientation?.unlock?.(); } catch (e) { /* noop */ }
      return;
    }
    setRotated(true);
    let locked = false;
    try {
      if (wrapperRef.current?.requestFullscreen) await wrapperRef.current.requestFullscreen();
      await window.screen.orientation.lock("landscape");
      locked = true;
    } catch (e) {
      locked = false;
    }
    setCssRotate(!locked);
  };

  const tiles = 1 + remoteUsers.length;
  const gridCols = tiles <= 1 ? "grid-cols-1" : tiles <= 4 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-2 sm:grid-cols-3";

  return (
    <div
      ref={wrapperRef}
      className={`h-full w-full flex flex-col bg-[#0F1015] ${cssRotate ? "rr-force-landscape" : ""}`}
    >
      <div className="px-3 sm:px-4 h-14 flex items-center justify-between border-b border-[#2E3242] shrink-0 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-2.5 h-2.5 rounded-full bg-red-500 pulse-glow shrink-0" />
          <h2 className="font-display font-bold text-sm text-gray-100 truncate">{title || "Live call"}</h2>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-gray-500 hidden sm:inline">{tiles} in call</span>
          <button
            onClick={() => enterFs(wrapperRef.current)}
            data-testid="meeting-fullscreen"
            className="w-8 h-8 rounded-md flex items-center justify-center border border-[#2E3242] bg-[#262936] text-gray-200 hover:bg-[#30333f]"
            title="Fullscreen meeting"
          >
            {isFs ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 sm:p-4">
        {joining ? (
          <div className="h-full flex flex-col items-center justify-center text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin mb-3 text-red-400" />
            Connecting…
          </div>
        ) : (
          <div className={`grid ${gridCols} gap-2 sm:gap-3`}>
            <Tile containerRef={localElRef} label={displayName} muted={!micOn} isLocal />
            {remoteUsers.map((u) => (
              <Tile
                key={u.uid}
                containerRef={(el) => (remoteEls.current[u.uid] = el)}
                label={`Guest ${String(u.uid).slice(-4)}`}
                muted={!u.hasAudio}
              />
            ))}
          </div>
        )}
      </div>

      <div className="shrink-0 border-t border-[#2E3242] bg-[#121318] py-2.5 sm:py-3 px-2 flex items-center justify-center gap-2 sm:gap-3 flex-wrap">
        <button
          data-testid="call-toggle-mic"
          onClick={toggleMic}
          className={`w-11 h-11 rounded-full flex items-center justify-center border transition-colors ${micOn ? "bg-[#262936] border-[#2E3242] text-gray-200 hover:bg-[#30333f]" : "bg-red-950/60 border-red-900/50 text-red-300"}`}
        >
          {micOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
        </button>
        <button
          data-testid="call-toggle-cam"
          onClick={toggleCam}
          className={`w-11 h-11 rounded-full flex items-center justify-center border transition-colors ${camOn ? "bg-[#262936] border-[#2E3242] text-gray-200 hover:bg-[#30333f]" : "bg-red-950/60 border-red-900/50 text-red-300"}`}
        >
          {camOn ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
        </button>
        <button
          data-testid="call-toggle-screen"
          onClick={toggleScreen}
          className={`w-11 h-11 rounded-full flex items-center justify-center border transition-colors ${sharing ? "bg-red-950/60 border-red-900/50 text-red-300" : "bg-[#262936] border-[#2E3242] text-gray-200 hover:bg-[#30333f]"}`}
        >
          <MonitorUp className="w-5 h-5" />
        </button>
        <button
          data-testid="call-rotate"
          onClick={toggleRotate}
          className={`md:hidden w-11 h-11 rounded-full flex items-center justify-center border transition-colors ${rotated ? "bg-red-950/60 border-red-900/50 text-red-300" : "bg-[#262936] border-[#2E3242] text-gray-200 hover:bg-[#30333f]"}`}
          title="Rotate to landscape"
        >
          <RotateCw className="w-5 h-5" />
        </button>
        <button
          data-testid="call-leave"
          onClick={leave}
          className="px-4 sm:px-5 h-11 rounded-full flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white font-semibold transition-colors"
        >
          <PhoneOff className="w-5 h-5" /> <span className="hidden sm:inline">Leave</span>
        </button>
      </div>
    </div>
  );
}
